import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import {
    OrganizationConnectionGroupBy,
    OrganizationIntegrationAccountItem,
} from "@common/schemas/organization-connection-groups.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import { OrganizationConnectionFilters } from "@common/schemas/organization-connection-filters.schema";
import { OrganizationIntegrationStatus } from "@common/helpers/organizationIntegrationStatus";
import { FilterableConnectionProvider } from "@common/schemas/organization-unified-connection.schema";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { GroupBySelect } from "@design-system/ViewTable/grouping/GroupBySelect";
import { SortSelect } from "@design-system/ViewTable/sorting/SortSelect";
import { useGroupedView } from "@design-system/ViewTable/grouping/useGroupedView";
import { withExpandColumnFirst } from "@design-system/ViewTable/grouping/expandColumnOrder";
import {
    GROUP_ROW_ID_PREFIX,
    GroupByRow,
    GroupChildrenState,
    GroupDescriptor,
} from "@design-system/ViewTable/grouping/groupByRowTypes";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { View, ViewSchema } from "@common/types/views/views";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { HStack } from "@design-system/HStack/HStack";
import {
    GROUP_ITEMS_PAGE_SIZE,
    useOrgConnectionGroups,
    useOrgIntegrationWorkspaces,
} from "@api/organizationIntegration.api";
import { useUser } from "@hooks/useUser";
import { AddIntegrationTrigger } from "./AddIntegrationTrigger";
import { IntegrationsBulkActionsFooter } from "./IntegrationsBulkActionsFooter";
import { selectedAssignmentTargets } from "./selectedAssignmentTargets";
import { selectedReminderTargets } from "./selectedReminderTargets";
import {
    pruneStaleGroupHeaders,
    selectedGroupKeys,
    selectedItemRowIds,
    syncGroupSelection,
    unselectedRowIdsInGroup,
    ChildrenByKey,
} from "./integrationsRowSelection";
import { OrganizationIntegrationsContext } from "./OrganizationIntegrationsContext";
import { useAssignmentStaging } from "./useAssignmentStaging";
import {
    IntegrationsGroupByColumn,
    BULK_SELECT_VIEW_OPTIONS,
    integrationsGroupByColumns,
    integrationsGroupByView,
    INTEGRATIONS_GROUP_BY_OPTIONS,
} from "./integrationsGroupByConfig";
import {
    DEFAULT_INTEGRATIONS_SORT,
    INTEGRATIONS_SORT_OPTIONS,
    IntegrationsItemsSortValue,
} from "./integrationsSortConfig";
import { accountItemRowId, getAccountItemRowId } from "./integrationsRowId";
import { withActionsColumnLast } from "./integrationsViewNormalization";
import { IntegrationRowActionsContext } from "./rowActions/IntegrationRowActionsContext";
import { OrgIntegrationReconnectProvider } from "./rowActions/OrgIntegrationReconnectProvider";
import { GroupChildrenLoader } from "./GroupChildrenLoader";
import { useGroupChildrenControls } from "./useGroupChildrenControls";
import { IntegrationFiltersControls } from "./IntegrationFiltersControls";
import styles from "./IntegrationsPage.module.scss";

const isGroupExpanded = (expanded: ExpandedState, groupKey: string): boolean =>
    expanded === true || !!expanded[`${GROUP_ROW_ID_PREFIX}${groupKey}`];

export const GroupedIntegrationsTable = ({
    organizationId,
}: {
    organizationId: string;
}) => {
    const [groupBy, setGroupBy] =
        useState<OrganizationConnectionGroupBy>("login");
    const [groupsPage, setGroupsPage] = useState(0);
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [statuses, setStatuses] = useState<OrganizationIntegrationStatus[]>(
        [],
    );
    const [providers, setProviders] = useState<FilterableConnectionProvider[]>(
        [],
    );
    const [itemsSort, setItemsSort] = useState<IntegrationsItemsSortValue>(
        DEFAULT_INTEGRATIONS_SORT,
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [childrenByKey, setChildrenByKey] = useState<ChildrenByKey>({});
    const [cachedView, setCachedView] = useOrganizationUserCache<View>(
        ORGANIZATION_USER_CACHE_KEYS.ORGANIZATION_INTEGRATIONS_PAGE_VIEW_CACHE_KEY,
        ViewSchema,
    );
    const [view, setView] = useState<View>(
        cachedView ?? integrationsGroupByView,
    );
    const tableRef = useRef<ViewTableHandle>(null);
    const { registerControls, unregisterControls, loadMore, retry } =
        useGroupChildrenControls();

    const staging = useAssignmentStaging();
    const workspacesQuery = useOrgIntegrationWorkspaces(organizationId);
    const workspaces = workspacesQuery.data?.workspaces;

    const currentUser = useUser();
    const [remindedConnectionIds, setRemindedConnectionIds] = useState<
        ReadonlySet<string>
    >(() => new Set());

    // A checked group header sits in the selection next to the children it
    // cascaded to. It stands for those rows rather than being one itself, so
    // everything user-facing counts items only.
    const selectedRowIds = useMemo(
        () => selectedItemRowIds(rowSelection),
        [rowSelection],
    );

    const groupKeyByRowId = useMemo(() => {
        const map = new Map<string, string>();
        for (const [groupKey, state] of Object.entries(childrenByKey)) {
            for (const item of state?.items ?? []) {
                map.set(accountItemRowId(item), groupKey);
            }
        }
        return map;
    }, [childrenByKey]);

    // Ticking a connection means all of its accounts, so header changes are
    // mirrored onto the children the table hasn't rendered. Deselecting a
    // loaded child then drops its (now partial) header via
    // `pruneStaleGroupHeaders`, so the adoption effect below stops re-selecting
    // it — without this a ticked header keeps pulling the row back in.
    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            setRowSelection((previous) => {
                const next =
                    typeof updater === "function" ? updater(previous) : updater;
                const synced = syncGroupSelection({
                    previous,
                    next,
                    childrenByKey,
                });
                return pruneStaleGroupHeaders({
                    selection: synced,
                    groupKeyByRowId,
                });
            });
        },
        [childrenByKey, groupKeyByRowId],
    );

    const pendingGroupKeys = useMemo(
        () => selectedGroupKeys(rowSelection),
        [rowSelection],
    );
    const reminderTargets = useMemo(
        () =>
            selectedReminderTargets({
                rowIds: selectedRowIds,
                childrenByKey,
                currentUserId: currentUser?.id ?? null,
            }),
        [selectedRowIds, childrenByKey, currentUser?.id],
    );
    const assignmentTargets = useMemo(
        () =>
            selectedAssignmentTargets({
                rowIds: selectedRowIds,
                childrenByKey,
                pendingByAccountId: staging.pendingByAccountId,
            }),
        [selectedRowIds, childrenByKey, staging.pendingByAccountId],
    );

    const clearSelection = useCallback(() => setRowSelection({}), []);

    const connectionIdByRowId = useMemo(() => {
        const map = new Map<string, string>();
        for (const state of Object.values(childrenByKey)) {
            for (const item of state?.items ?? []) {
                map.set(accountItemRowId(item), item.connection.id);
            }
        }
        return map;
    }, [childrenByKey]);

    const accountIdByRowId = useMemo(() => {
        const map = new Map<string, number>();
        for (const state of Object.values(childrenByKey)) {
            for (const item of state?.items ?? []) {
                map.set(accountItemRowId(item), item.account.id);
            }
        }
        return map;
    }, [childrenByKey]);

    // Only the logins that actually got an email are dropped; the rest stay
    // selected so a partial failure is retryable without re-picking rows. Any
    // header they sat under is dropped as well, otherwise the adoption effect
    // below would re-select the rows that just got an email.
    const handleRemindersSent = useCallback(
        (connectionIds: string[]) => {
            const sentIds = new Set(connectionIds);
            setRowSelection((previous) =>
                pruneStaleGroupHeaders({
                    selection: Object.fromEntries(
                        Object.entries(previous).filter(
                            ([rowId]) =>
                                !sentIds.has(
                                    connectionIdByRowId.get(rowId) ?? "",
                                ),
                        ),
                    ),
                    groupKeyByRowId,
                }),
            );
        },
        [connectionIdByRowId, groupKeyByRowId],
    );

    // Same idea for assignment: an account that landed is done, while one that
    // failed stays selected so the user can retry it.
    const handleAccountsAssigned = useCallback(
        (accountIds: number[]) => {
            const assignedIds = new Set(accountIds);
            setRowSelection((previous) =>
                pruneStaleGroupHeaders({
                    selection: Object.fromEntries(
                        Object.entries(previous).filter(([rowId]) => {
                            const accountId = accountIdByRowId.get(rowId);
                            return (
                                accountId === undefined ||
                                !assignedIds.has(accountId)
                            );
                        }),
                    ),
                    groupKeyByRowId,
                }),
            );
        },
        [accountIdByRowId, groupKeyByRowId],
    );

    const filters: OrganizationConnectionFilters = useMemo(
        () => ({ search, statuses, providers }),
        [search, statuses, providers],
    );

    const groupsQuery = useOrgConnectionGroups({
        organizationId,
        groupBy,
        page: groupsPage,
        filters,
    });
    const groupsResponse = groupsQuery.data;
    const groups: GroupDescriptor[] | null = groupsResponse?.groups ?? null;

    // Selection is cleared alongside the loaded rows: selected ids are only
    // resolvable through `childrenByKey`, so keeping them past a reset would
    // leave a count that points at rows nobody can act on.
    const resetLoadedChildren = useCallback(() => {
        setExpanded({});
        setRowSelection({});
        setChildrenByKey({});
    }, []);

    const goToGroupsPage = useCallback(
        (page: number) => {
            setGroupsPage(page);
            resetLoadedChildren();
        },
        [resetLoadedChildren],
    );

    const resetForGrouping = useCallback(() => {
        goToGroupsPage(0);
    }, [goToGroupsPage]);

    // TablePageHeader clears to "", which would be sent as `search=`; normalize
    // so a cleared box omits the param exactly like the initial mount does.
    const handleSearchChange = useCallback(
        (next: string | undefined) => {
            setSearch(next?.trim() ? next : undefined);
            resetForGrouping();
        },
        [resetForGrouping],
    );

    const handleStatusesChange = useCallback(
        (next: OrganizationIntegrationStatus[]) => {
            setStatuses(next);
            resetForGrouping();
        },
        [resetForGrouping],
    );

    const handleProvidersChange = useCallback(
        (next: FilterableConnectionProvider[]) => {
            setProviders(next);
            resetForGrouping();
        },
        [resetForGrouping],
    );

    const handleViewChange = useCallback(
        (next: View) => {
            setView(next);
            setCachedView(next);
        },
        [setCachedView],
    );

    const handleGroupByChange = useCallback(
        (next: OrganizationConnectionGroupBy | null) => {
            if (next === null || next === groupBy) {
                return;
            }
            setGroupBy(next);
            resetForGrouping();
        },
        [groupBy, resetForGrouping],
    );

    // Sorting reorders rows within groups but never the groups themselves, so
    // the loaded children are cleared while the groups page is kept.
    const handleItemsSortChange = useCallback(
        (next: IntegrationsItemsSortValue | null) => {
            if (next === null || next === itemsSort) {
                return;
            }
            setItemsSort(next);
            resetLoadedChildren();
        },
        [itemsSort, resetLoadedChildren],
    );

    const itemsSortQueryValue =
        itemsSort === DEFAULT_INTEGRATIONS_SORT ? undefined : itemsSort;

    // A mutation can shrink pageCount below the current page (the page then
    // returns no groups) — clamp back to the last valid page.
    const pageCount = groupsResponse?.pageCount;
    useEffect(() => {
        if (
            pageCount !== undefined &&
            groupsPage > 0 &&
            groupsPage >= pageCount
        ) {
            goToGroupsPage(Math.max(pageCount - 1, 0));
        }
    }, [pageCount, groupsPage, goToGroupsPage]);

    const handleChildState = useCallback(
        (
            groupKey: string,
            state: GroupChildrenState<OrganizationIntegrationAccountItem>,
        ) => {
            setChildrenByKey((prev) => ({ ...prev, [groupKey]: state }));
        },
        [],
    );

    // A ticked connection keeps pulling its remaining pages and adopts each one
    // into the selection: the header stands for the whole group, not just the
    // rows that happened to be loaded when it was ticked.
    useEffect(() => {
        for (const groupKey of pendingGroupKeys) {
            const state = childrenByKey[groupKey];
            if (state === undefined || !state.isSettled) {
                continue;
            }
            if (state.hasMore) {
                if (!state.isLoadingMore) {
                    loadMore(groupKey);
                }
                continue;
            }
            const missingRowIds = unselectedRowIdsInGroup({
                selection: rowSelection,
                childrenByKey,
                groupKey,
            });
            if (missingRowIds.length > 0) {
                setRowSelection((previous) => ({
                    ...previous,
                    ...Object.fromEntries(
                        missingRowIds.map((rowId) => [rowId, true]),
                    ),
                }));
            }
        }
    }, [pendingGroupKeys, childrenByKey, rowSelection, loadMore]);

    // Assigning re-buckets rows when the table is grouped by client, so the
    // group list changes shape and the expanded keys no longer line up — the
    // mutation's invalidation alone would leave stale groups on screen.
    const handleAssignmentsCommitted = useCallback(() => {
        if (groupBy === "client") {
            resetForGrouping();
        }
    }, [groupBy, resetForGrouping]);

    const assignmentContext = useMemo(
        () => ({
            organizationId,
            workspaces: workspaces ?? [],
            assignmentMode: "commit" as const,
            onAssignmentsCommitted: handleAssignmentsCommitted,
            ...staging,
        }),
        [organizationId, workspaces, handleAssignmentsCommitted, staging],
    );

    const markConnectionReminded = useCallback((connectionId: string) => {
        setRemindedConnectionIds(
            (previous) => new Set([...previous, connectionId]),
        );
    }, []);

    // Removing an account changes the group's count and can empty it out, so the
    // loaded children are rebuilt rather than patched.
    const handleAccountRemoved = useCallback(() => {
        resetForGrouping();
    }, [resetForGrouping]);

    const rowActionsContext = useMemo(
        () => ({
            organizationId,
            remindedConnectionIds,
            markConnectionReminded,
            onAccountRemoved: handleAccountRemoved,
        }),
        [
            organizationId,
            remindedConnectionIds,
            markConnectionReminded,
            handleAccountRemoved,
        ],
    );

    const noop = useCallback(() => undefined, []);

    const groupedViewProps = useGroupedView<
        OrganizationIntegrationAccountItem,
        IntegrationsGroupByColumn
    >({
        groups,
        childrenByKey,
        columns: integrationsGroupByColumns,
        getItemRowId: getAccountItemRowId,
        onExpandGroup: noop,
        onLoadMore: loadMore,
        onRetry: retry,
        expanded,
        onExpandedChange: setExpanded,
        skeletonRowCount: GROUP_ITEMS_PAGE_SIZE,
        loadMorePageSize: GROUP_ITEMS_PAGE_SIZE,
        rowSelection,
        onRowSelectionChange: handleRowSelectionChange,
    });

    const renderFooter = () => {
        if (selectedRowIds.length > 0) {
            return (
                <IntegrationsBulkActionsFooter
                    organizationId={organizationId}
                    selectedCount={selectedRowIds.length}
                    targets={reminderTargets}
                    assignmentTargets={assignmentTargets}
                    onClearSelection={clearSelection}
                    onRemindersSent={handleRemindersSent}
                    onAccountsAssigned={handleAccountsAssigned}
                />
            );
        }
        if (!groupsResponse) {
            return undefined;
        }
        return (
            <CommandIsland sticky>
                {groupsResponse.pageCount > 1 ? (
                    <Pagination
                        size="md"
                        pageCount={groupsResponse.pageCount}
                        currentPage={groupsPage + 1}
                        onPageChange={(page) => goToGroupsPage(page - 1)}
                    />
                ) : (
                    <CommandIslandTotal>
                        {pluralizeWithCount(
                            "account",
                            groupsResponse.totalAccounts,
                        )}
                    </CommandIslandTotal>
                )}
            </CommandIsland>
        );
    };

    // Normalized on read, not only on write, so views cached before these
    // invariants existed are corrected too.
    const normalizeView = useCallback(
        (next: View) =>
            withExpandColumnFirst({
                view: withActionsColumnLast({ view: next }),
                config: groupedViewProps.config,
            }),
        [groupedViewProps.config],
    );

    const orderedView = useMemo(
        () => normalizeView(view),
        [normalizeView, view],
    );

    // Selection rides on top of whatever columns the user has, rather than
    // swapping in a fixed view that would drop their layout.
    const tableView = useMemo(
        () => ({
            ...orderedView,
            options: {
                ...orderedView.options,
                ...BULK_SELECT_VIEW_OPTIONS,
            },
        }),
        [orderedView],
    );

    // Normalized the same way as `orderedView`: ColumnSettings deep-compares the
    // two to decide whether columns are "modified", and it strips only `size` —
    // so an unnormalized default would differ by the expand column's pin and
    // report a fresh view as modified forever.
    const orderedDefaultView = useMemo(
        () => normalizeView(integrationsGroupByView),
        [normalizeView],
    );

    if (groupsQuery.isError) {
        return (
            <div className={styles.emptyState}>
                <EmptyState
                    header="Couldn't load connections"
                    body="Something went wrong loading this organization's integrations. Please try again."
                />
            </div>
        );
    }

    return (
        <OrganizationIntegrationsContext.Provider value={assignmentContext}>
            <IntegrationRowActionsContext.Provider value={rowActionsContext}>
                <OrgIntegrationReconnectProvider
                    organizationId={organizationId}
                >
                    <TablePageHeader
                        isFullPage
                        search={search}
                        onSearchChange={handleSearchChange}
                        beforeFiltersChildren={
                            <IntegrationFiltersControls
                                statuses={statuses}
                                providers={providers}
                                onStatusesChange={handleStatusesChange}
                                onProvidersChange={handleProvidersChange}
                            />
                        }
                    >
                        <HStack gap={8} align="center">
                            <CollapseAllButton
                                expanded={expanded}
                                tableRef={tableRef}
                            />
                            <ColumnSettings
                                view={orderedView}
                                viewConfig={groupedViewProps.config}
                                defaultView={orderedDefaultView}
                                onViewChange={handleViewChange}
                            />
                            <GroupBySelect
                                value={groupBy}
                                options={INTEGRATIONS_GROUP_BY_OPTIONS}
                                onValueChange={handleGroupByChange}
                            />
                            <SortSelect
                                value={itemsSort}
                                defaultKey={DEFAULT_INTEGRATIONS_SORT}
                                options={INTEGRATIONS_SORT_OPTIONS}
                                onValueChange={handleItemsSortChange}
                            />
                            <AddIntegrationTrigger
                                organizationId={organizationId}
                            />
                        </HStack>
                    </TablePageHeader>

                    {(groups ?? []).map((group) => (
                        <GroupChildrenLoader
                            key={group.key}
                            organizationId={organizationId}
                            groupBy={groupBy}
                            groupKey={group.key}
                            // A ticked group loads even while collapsed: its accounts
                            // have to exist client-side before they can be selected.
                            enabled={
                                isGroupExpanded(expanded, group.key) ||
                                pendingGroupKeys.includes(group.key)
                            }
                            filters={filters}
                            sort={itemsSortQueryValue}
                            onStateChange={handleChildState}
                            registerControls={registerControls}
                            unregisterControls={unregisterControls}
                        />
                    ))}

                    <ViewTable<
                        GroupByRow<OrganizationIntegrationAccountItem>,
                        IntegrationsGroupByColumn,
                        never
                    >
                        {...groupedViewProps}
                        ref={tableRef}
                        view={tableView}
                        onViewChange={handleViewChange}
                        fixedTopOffsetPx
                        emptyView={() => (
                            <div className={styles.emptyState}>
                                <EmptyState
                                    header="No connections yet"
                                    body="Connect a bank to import its accounts into this organization."
                                />
                            </div>
                        )}
                        footer={renderFooter()}
                    />
                </OrgIntegrationReconnectProvider>
            </IntegrationRowActionsContext.Provider>
        </OrganizationIntegrationsContext.Provider>
    );
};
